import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Users, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { useJobActions } from "@/pages/admin/useJobActions";
import { shortDay } from "@/components/MultiDatePicker";
import { recurLabel } from "@/components/RecurringFields";
import { DeleteCantiereButton } from "@/components/DeleteCantiereButton";
import { useAuth } from "@/lib/auth";

const STATE = {
  da_assegnare: ["Da assegnare", "bg-amber-100 text-amber-800"],
  da_iniziare: ["Da iniziare", "bg-sky-100 text-sky-800"],
  in_corso: ["In corso", "bg-[#eef5e4] text-[#3d6b16]"],
  completato: ["Completato", "bg-slate-100 text-slate-600"],
};

function Row({ c, i, onOpen }) {
  const [label, cls] = STATE[c.state];
  return (
    <button onClick={onOpen} className="grid w-full gap-2 px-5 py-4 text-left transition-colors hover:bg-slate-50 md:grid-cols-[1.4fr_1fr_auto] md:items-center" data-testid={`cantiere-row-${i}`}>
      <div>
        <p className="font-semibold text-slate-900">{c.name}{c.committente && <span className="ml-2 text-sm font-normal text-slate-500">{c.committente}</span>}</p>
        <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{c.address}{c.city ? `, ${c.city}` : ""}</p>
      </div>
      <div className="text-sm">
        {c.recurring ? <p data-testid={`cantiere-recurring-${i}`}><b>Ripetitivo</b> · {recurLabel(c.recurring)}{c.recurring.stopped ? " · terminato" : ""}</p>
          : <p className="capitalize">{shortDay(c.dates[0])}{c.dates.length > 1 ? ` → ${shortDay(c.dates[c.dates.length - 1])}` : ""} · {c.days_done}/{c.days_total} giorni</p>}
        <p className="flex items-center gap-1 text-slate-500"><Users className="h-3.5 w-3.5" />{c.technician_name || "Nessun responsabile"}{c.helpers.length ? ` + ${c.helpers.map((h) => h.name).join(", ")}` : ""}</p>
      </div>
      <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`cantiere-state-${i}`}>{label}</span>
    </button>
  );
}

export default function AdminCantieri() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/cantieri").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const actions = useJobActions(load);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const open = async (c) => actions.openDetail((await api.get(`/jobs/${c.job_id}`)).data);
  return (
    <div>
      <PageHeader eyebrow="Lavori programmati" title="Cantieri">
        <Link to="/admin/cantieri/nuovo"><Button className="rounded-full" data-testid="new-cantiere-button"><Plus className="h-4 w-4" /> Nuovo cantiere</Button></Link>
      </PageHeader>
      <div className="divide-y rounded-2xl border bg-white" data-testid="cantieri-list">
        {items === null ? <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          : !items.length ? <p className="p-8 text-center text-sm text-slate-500" data-testid="cantieri-empty">Nessun cantiere. Crea il primo con "Nuovo cantiere".</p>
          : items.map((c, i) => (
            <div key={c.series_id || c.job_id} className="flex flex-col md:flex-row md:items-center">
              <div className="flex-1"><Row c={c} i={i} onOpen={() => open(c)} /></div>
              {c.series_id && <div className="mx-5 mb-4 flex gap-2 md:my-0 md:ml-0">
                <Link to={`/admin/cantieri/${c.series_id}/rendiconto`} data-testid={`cantiere-report-link-${i}`}>
                <Button size="sm" variant="outline" className="rounded-full"><ClipboardList className="h-4 w-4" /> Rendiconto</Button></Link>
                {isAdmin && <DeleteCantiereButton c={c} i={i} onDone={load} />}</div>}
            </div>
          ))}
      </div>
      {actions.ui}
    </div>
  );
}
